///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { ActionsToolbarModeEnum, GtthStepGroupEnum, MapTypeEnum, ProjectUserSessionDTO } from '../../../../../generated/shared-types.model';
import { GtthProgressWindowModel } from '../../../../project-windows/progress-window/progress-window.model';
import { PARAM_BBOX } from '../../../../../shared/constants/url-params.constants';
import { GtthPosition } from '../../../entities/position';
import { GtthToolbarModeChangedCommand } from '../toolbar/toolbar-mode-changed-command';
import { SectorFeature } from '../../../entities/sector/sector-feature';

export class GtthApplyViewStateCommand extends GtthAbstractCommand {
    private lockedBySameUser = false;

    getCommandName(): string {
        return 'GtthApplyViewStateCommand';
    }

    create(lockedBySameUser: boolean): this {
        this.lockedBySameUser = lockedBySameUser;
        return this;
    }

    doCommand(): void {
        const userSession = this.historyService.projectUserSessionService.getSessionObject();
        this.applyChanges(userSession).then((result) => this.commandResolve(result));
    }

    private async applyChanges(userSession: ProjectUserSessionDTO): Promise<boolean> {
        this.zoomAndCenterMap(userSession.bbox);
        const mapType = userSession.mapType ?? MapTypeEnum.ROAD_MAP;
        this.historyService.map.setMapTypeId(mapType);

        const selectedStep = this.resolveSelectedSteps(userSession.stepGroupNumber);
        const actionMode = this.resolveActionMode(selectedStep, userSession.actionMode);
        this.historyService.selectedStepNumber = selectedStep;
        await this.historyService.doCommandBypassHistory(this.historyService.command(GtthToolbarModeChangedCommand).create(actionMode));

        if (
            userSession.activeModalDialog &&
            (!userSession.activeModalDialog.designRequired || this.historyService.logicalTopology.isShowPlanningMode)
        ) {
            this.historyService.router.navigate(['/', { outlets: { popup: userSession.activeModalDialog.request } }]);
        }

        if (userSession.showDesignIterations && this.historyService.logicalTopology.designIterationsChartData?.length > 0) {
            this.historyService.progressWindowService.show(GtthProgressWindowModel.createMultipleFromHistory(this.historyService));
        }

        if (userSession.activeInteractiveDialog) {
            await userSession.activeInteractiveDialog
                .filter((activeWindow) => !activeWindow.designRequired || this.historyService.logicalTopology.isShowPlanningMode)
                .reduce(async (previousValue, activeWindow) => {
                    const queryParams = {};
                    queryParams[PARAM_BBOX] = activeWindow.bbox.join(',');
                    await previousValue;

                    return new Promise((resolve) => {
                        setTimeout(async () => {
                            await this.historyService.router.navigate(['/', { outlets: { popup: activeWindow.request } }], {
                                queryParams
                            });
                            resolve();
                        }, 0);
                    });
                }, Promise.resolve());
        }

        if (this.lockedBySameUser && userSession.selectedEntities) {
            this.selectEntities(userSession);
        }

        return true;
    }

    private selectEntities(userSession: ProjectUserSessionDTO) {
        if (userSession.selectedEntities.selectedNodes) {
            userSession.selectedEntities.selectedNodes.forEach((nodeName) =>
                this.historyService.logicalTopology.addSelectedStationByName(nodeName)
            );
        }

        if (userSession.selectedEntities.selectedLinkIds) {
            userSession.selectedEntities.selectedLinkIds.forEach((linkId) =>
                this.historyService.logicalTopology.addSelectedLinkById(linkId)
            );
        }

        if (userSession.selectedEntities.selectedSectors?.length) {
            const aggregatedSectorIndex = userSession.selectedEntities.selectedSectors[0];
            const [sectorIndex, sectorInDevice] = SectorFeature.getSectorIndexesFromAggregatedIndex(aggregatedSectorIndex);
            const sector = this.historyService.logicalTopology.getSector(sectorIndex, sectorInDevice);
            if (sector) {
                sector.select();
            }
        }

        if (userSession.activeSelectArea?.length > 0) {
            setTimeout(async () => {
                this.historyService.selectAreaService.createSelectArea(userSession.activeSelectArea, false);
            }, 0);
        }

        if (userSession.infoWindowConfig) {
            const point = GtthPosition.fromValues(
                userSession.infoWindowConfig.position.longitude,
                userSession.infoWindowConfig.position.latitude
            );

            setTimeout(() => {
                this.historyService.infoWindowsService.openInfoWindow(
                    userSession.infoWindowConfig.entityId,
                    point,
                    userSession.infoWindowConfig.type,
                    false
                );
            }, 0);
        }
    }

    private zoomAndCenterMap(bbox: number[]): void {
        if (bbox[0] && bbox[1]) {
            this.historyService.mapSetCenterAndZoom(bbox[1], bbox[0], bbox[2]);
        } else if (this.historyService.logicalTopology.getTerminalStationsCount() > 0) {
            this.historyService.logicalTopology.getMapBounds().then((newBounds) => {
                this.historyService.mapFitBounds(newBounds);
            });
        } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                this.historyService.mapSetCenterAndZoom(position.coords.latitude, position.coords.longitude, 11);
            });
        }
    }

    private resolveSelectedSteps(stemGroup: GtthStepGroupEnum): GtthStepGroupEnum {
        if (this.historyService.logicalTopology.isShowPlanningMode) {
            return stemGroup;
        } else {
            return stemGroup === GtthStepGroupEnum.LOCATIONS_AND_LOSES || stemGroup === GtthStepGroupEnum.PLAN_AND_OPTIMISE
                ? stemGroup
                : GtthStepGroupEnum.LOCATIONS_AND_LOSES;
        }
    }

    private resolveActionMode(stemGroup: GtthStepGroupEnum, actionMode: ActionsToolbarModeEnum): ActionsToolbarModeEnum {
        if (
            this.historyService.isEditModeEnabled &&
            (stemGroup === GtthStepGroupEnum.LOCATIONS_AND_LOSES || actionMode === ActionsToolbarModeEnum.SELECT_AREA_MODE)
        ) {
            return actionMode;
        } else {
            return ActionsToolbarModeEnum.POINTER;
        }
    }
}
